// legendHierarchy.jsx
import React, { useState, useEffect, useCallback } from 'react';
import Button from './widgets/button';
import { useTheme } from '../theme/ThemeContext';
import * as d3 from 'd3';

const LegendHierarchy = ({ uniqueCategories, colorParentSelected, setColorParentSelected, colors, colorDict, scatterplot, possibleColorDict, setPendingSelection, styles }) => {
    // selectedDepth based on colorParentSelected
    const initialDepth = colorParentSelected[0]?.split(':').length || 1;
    // const initialDepth = 2;
    const [selectedDepth, setSelectedDepth] = useState(initialDepth);

    // currentPath with colorParentSelected
    const [currentPath, setCurrentPath] = useState(colorParentSelected);
    const { theme } = useTheme();

    // useEffect to handle initial expansion
    useEffect(() => {
        if (colorParentSelected.length > 0 && !currentPath.length) {
            setCurrentPath(colorParentSelected);
            setSelectedDepth(colorParentSelected[0].split(':').length);
        }
    }, [colorParentSelected]);

    const divStyleLegendHierarchy = {
        ...styles,
        overflow: 'auto',
        maxHeight: '80vh',
        color: '#dadada'
    };

    const isPartOfCurrentPath = useCallback((category) => {
        if (currentPath.length === 1) return true;
        const categoryPath = category.split(':');

        if (categoryPath.length - 1 === selectedDepth) return true;

        return currentPath.some(pathItem => {
            const pathParts = pathItem.split(':');
            return categoryPath.length <= pathParts.length &&
                pathParts.slice(0, categoryPath.length).join(':') === category;
        });
    }, [currentPath, selectedDepth]);

    const handleReset = useCallback(() => {
        setColorParentSelected([]);
        setSelectedDepth(1);
        setCurrentPath([]);
        if (scatterplot?.select) {
            scatterplot.select([]);
        }
    }, [setColorParentSelected, scatterplot]);


    const handleLegendClick = useCallback((category) => {
        if (category === 'user') {
            setColorParentSelected(['user']);
            setSelectedDepth(1);
            setCurrentPath(['user']);
            setPendingSelection({
                category: 'user',
                includeUserPoints: true,
                userOnly: true
            });
            return;
        }

        if (!category.includes(':')) {
            setColorParentSelected([]);
            setSelectedDepth(1);
            setCurrentPath([]);
            setPendingSelection(null);
            if (scatterplot?.select) {
                scatterplot.select([]);
            }
            return;
        }

        const splitCategory = category.split(':');

        setCurrentPath(prev => {
            if (prev.includes(category)) {
                return prev.filter(p => !p.startsWith(category));
            }

            const newPaths = prev.filter(p => {
                const pSplit = p.split(':');
                if (pSplit.length < splitCategory.length) return true;
                if (pSplit.length === splitCategory.length) {
                    const parentPath = pSplit.slice(0, -1).join(':');
                    const selectedParentPath = splitCategory.slice(0, -1).join(':');
                    return parentPath !== selectedParentPath;
                }
                return false;
            });

            return [...newPaths, category];
        });

        setSelectedDepth(splitCategory.length);
        setColorParentSelected(prev => {
            const filteredPrev = prev.filter(p => {
                const pSplit = p.split(':');
                if (pSplit.length < splitCategory.length) return true;
                if (pSplit.length === splitCategory.length) {
                    const parentPath = pSplit.slice(0, -1).join(':');
                    const selectedParentPath = splitCategory.slice(0, -1).join(':');
                    return parentPath !== selectedParentPath;
                }
                return false;
            });

            if (!filteredPrev.includes(category)) {
                return [...filteredPrev, category];
            }
            return filteredPrev;
        });

        // set selection without including user points for regular categories
        setPendingSelection({
            category,
            includeUserPoints: false
        });
    }, [setColorParentSelected, setPendingSelection, scatterplot]);

    const buildHierarchy = (entries) => {
        const hierarchy = {};
        const baseColors = {
            'Environmental': [144, 238, 144],
            'Host-associated': [255, 228, 196],
            'Engineered': [188, 143, 143],
            'Mixed': [128, 128, 128]
        };

        // add user category to entries if it exists in colorDict
        // if (colorDict['user']) {
        //     entries = [...entries, ['user', colorDict['user']]];
        // }
        // entries = entries.filter(([category]) => !category.startsWith('user_'));
        entries = entries.filter(([category]) => !category.startsWith('user'));

        // const customOrder = ['Host-associated', 'Environmental', 'Engineered', 'Mixed', 'user'];
        const customOrder = ['Host-associated', 'Environmental', 'Engineered', 'Mixed'];

        entries.sort(([a], [b]) => {
            const aCategory = a.split(':')[1] || a;
            const bCategory = b.split(':')[1] || b;
            return customOrder.indexOf(aCategory) - customOrder.indexOf(bCategory);
        });

        const directSamplePaths = new Set(entries.map(([category]) => category));

        const getColorForPath = (fullPath) => {
            const parts = fullPath.split(':');
            if (possibleColorDict[fullPath]) {
                return possibleColorDict[fullPath];
            }

            if (parts.length === 2 && baseColors[parts[1]]) {
                return baseColors[parts[1]];
            }

            while (parts.length > 1) {
                parts.pop();
                const ancestorPath = parts.join(':');
                if (colorDict[ancestorPath]) {
                    return colorDict[ancestorPath];
                }
                if (parts.length === 2 && baseColors[parts[1]]) {
                    return baseColors[parts[1]];
                }
            }

            return null;
        };

        entries.forEach(([category, _]) => {
            const parts = category.split(':');
            let current = hierarchy;
            let currentPath = '';

            // special handling for user category
            if (category === 'user') {
                current['user'] = {
                    color: colorDict['user'],
                    originalColor: colorDict['user'],
                    children: {},
                    depth: 0,
                    fullPath: 'user',
                    hasDirectSamples: true
                };
                return;
            }

            parts.forEach((part, index) => {
                currentPath = index === 0 ? part : `${currentPath}:${part}`;
                if (!current[part]) {
                    const nodeColor = getColorForPath(currentPath);
                    const hasDirectSamples = directSamplePaths.has(currentPath);

                    current[part] = {
                        color: nodeColor,
                        originalColor: nodeColor,
                        children: {},
                        depth: index,
                        fullPath: currentPath,
                        hasDirectSamples: hasDirectSamples
                    };
                }
                current = current[part].children;
            });
        });

        return hierarchy;
    };


    useEffect(() => {
        const container = d3.select('#legendContainer');
        // if (!container.empty() && colorDict) {
        // console.log(colorDict);
        // if (!container.empty() && colorDict) {
        if (!container.empty() && colorDict && Object.keys(colorDict).length > 0) {
            container.selectAll('*').remove();

            const colorEntries = Object.entries(colorDict);
            const hierarchy = buildHierarchy(colorEntries);

            const columnWidth = 140;
            // const columnWidth = 240;
            const itemHeight = 30;
            const itemSpacing = 20;
            // const lineColor = '#dadada';
            const lineColor = theme.colors.text;
            const fontSize = '12px';
            const textPadding = 20;

            const maxDepth = Math.max(...colorEntries.map(([cat]) => cat.split(':').length));
            const totalWidth = (maxDepth) * columnWidth;

            const depthCounts = new Array(maxDepth).fill(0);
            const countNodesAtDepth = (node, depth = 0) => {
                depthCounts[depth] = (depthCounts[depth] || 0) + 1;
                Object.values(node.children).forEach(child => countNodesAtDepth(child, depth + 1));
            };
            Object.values(hierarchy).forEach(node => countNodesAtDepth(node));
            const maxNodesAtAnyDepth = Math.max(...depthCounts);
            const totalHeight = maxNodesAtAnyDepth * itemSpacing + 20;

            const svg = container
                .append('svg')
                .attr('width', totalWidth)
                .attr('height', totalHeight);

            const textMeasurer = svg.append('text')
                .attr('visibility', 'hidden')
                .style('font-size', fontSize);

            const getTextWidth = (text) => {
                textMeasurer.text(text);
                return textMeasurer.node().getComputedTextLength();
            };

            const linesGroup = svg.append('g').attr('class', 'connecting-lines');



            const renderNodes = (node, depth = 0, index = 0, parentX = null, parentY = null, parentLabelWidth = null) => {
                const x = 10 + depth * columnWidth;
                const y = 20 + index * itemSpacing;

                const baseOpacity = 1; // Remove the direct samples check
                const selectionOpacity = node.fullPath ? (isPartOfCurrentPath(node.fullPath) ? 1 : 0.3) : 1;
                const finalOpacity = selectionOpacity;

                // console.log(node.fullPath);

                const group = svg.append('g')
                    .attr('transform', `translate(${x}, ${y})`)
                    .style('cursor', 'pointer')
                    .style('opacity', finalOpacity)
                    .on('click', () => {
                        if (node.fullPath) {
                            handleLegendClick(node.fullPath);
                        }
                        // handleLegendClick(node.fullPath);
                    });

                const label = node.fullPath ? node.fullPath.split(':').pop().replace(/_/g, ' ') : '';
                const currentLabelWidth = getTextWidth(label) + textPadding;

                if (parentX !== null && parentY !== null && parentLabelWidth !== null) {
                    const startX = parentX + parentLabelWidth;
                    const endX = x;
                    const middleX = startX + (endX - startX) / 2;

                    linesGroup.append('path')
                        .attr('d', `
                            M ${startX} ${parentY + 5}
                            H ${middleX}
                            V ${y + 5}
                            H ${endX}
                        `)
                        .attr('stroke', lineColor)
                        .attr('fill', 'none')
                        .attr('stroke-width', 1);
                }

                const displayColor = node.originalColor;

                group.append('rect')
                    .attr('x', 0)
                    .attr('y', 0)
                    .attr('width', 10)
                    .attr('height', 10)
                    .attr('fill', displayColor ? `rgb(${displayColor[0]}, ${displayColor[1]}, ${displayColor[2]})` : 'none');

                group.append('text')
                    .attr('x', 15)
                    .attr('y', 9)
                    .text(label)
                    // .style('fill', '#dadada')
                    .style('fill', theme.colors.text)
                    .style('font-size', fontSize);

                const childrenArray = Object.entries(node.children);
                childrenArray.forEach(([_, childNode], childIndex) => {
                    renderNodes(childNode, depth + 1, childIndex, x, y, currentLabelWidth);
                });
            };

            Object.values(hierarchy).forEach((node, index) => {
                renderNodes(node, 0, index);
            });

            textMeasurer.remove();
        }
    }, [colorDict, handleLegendClick, isPartOfCurrentPath, theme]);

    return (
        <div style={divStyleLegendHierarchy}>
            <div id="legendContainer"></div>
        </div>
    );
};

export default LegendHierarchy;
